// approvalDocumentLabel.ts — 🔒 2026-08-30 (Manak).
//
// Line-for-line port of the phone's
// mobile/apps/aura_sales/lib/features/manager/approval_document_label.dart.
// The phone and the web must never disagree about what a discount request is
// on, so this file mirrors that one exactly — same order, same fallbacks, same
// early `return null` for a type the server named but we do not understand.
//
// WHAT is this discount on? A quotation the rep is still negotiating, or an
// order the customer has already agreed to? Those are very different decisions
// and the card showed neither — only "Discount" and "With MD".
//
// The API already answers it: `documentType` is 'proposal' | 'sales_order' |
// 'invoice'. Measured live 2026-08-30 across 63 requests: 60 proposal,
// 1 sales_order, 1 invoice, 1 null. PURE — no React, no network.
// A Map, not an object literal: an object literal would answer
// `Object.prototype.constructor` for documentType 'constructor' and sail past
// the null check below. Dart's Map has no such inherited keys.
const TYPE_LABELS = new Map<string, string>([
  ['proposal', 'Proposal'],
  ['sales_order', 'Sales order'],
  ['invoice', 'Invoice'],
]);

const NUMBER_PREFIXES: Array<[string, string]> = [
  ['PROP-', 'Proposal'],
  ['SO-', 'Sales order'],
  ['INV-', 'Invoice'],
];

export interface ApprovalDocumentLabelInput {
  documentType?: string | null;
  proposalId?: string | null;
  salesOrderId?: string | null;
  invoiceId?: string | null;
  documentNumber?: string | null;
}

/**
 * The chip text, or null when we genuinely do not know.
 *
 * 🔒 Returns null rather than guessing. A wrong label on an approval card is
 * worse than no label — it changes how the approver reads the decision.
 */
export function approvalDocumentLabel(r: ApprovalDocumentLabelInput): string | null {
  // 1. The server's own answer wins, even over a contradicting id column.
  const t = (r.documentType ?? '').trim().toLowerCase();
  if (t.length > 0) {
    const byType = TYPE_LABELS.get(t);
    if (byType != null) return byType;
    // An unrecognised type is NOT a licence to guess from the ids — the server
    // told us something we do not understand, so say nothing.
    return null;
  }

  // 2. Older rows carry no documentType; use whichever id is set.
  if ((r.salesOrderId ?? '').trim().length > 0) return 'Sales order';
  if ((r.proposalId ?? '').trim().length > 0) return 'Proposal';
  if ((r.invoiceId ?? '').trim().length > 0) return 'Invoice';

  // 3. Last resort, the number. 🔒 This matters: the hold path DELETES the
  // sales order, so the id can point at a row that no longer exists while the
  // number survives on the request itself.
  const n = (r.documentNumber ?? '').trim().toUpperCase();
  for (const [prefix, label] of NUMBER_PREFIXES) {
    if (n.startsWith(prefix)) return label;
  }
  return null;
}
